/**
 * Сводка прогресса пользователя (docs/06-api.md, `/me/progress`).
 *
 * Идентификатор смотрящего входит в ключ запроса, как и в каталоге: после
 * входа под другим аккаунтом данные обязаны перезапроситься, а не показаться
 * от прежнего пользователя.
 */

import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { useViewerId } from './catalog'
import { authFetch } from './session'

export const progressTotalsSchema = z.object({
  tasks_total: z.number(),
  solved: z.number(),
  solved_without_hints: z.number(),
  attempted: z.number(),
  // Закрытых долгов на самостоятельное решение (FR-P8). Счётчик соседний
  // с «решено самостоятельно», а не поправка к нему: задача, решённая
  // когда-то по подсказкам, такой и остаётся.
  review_solved: z.number(),
})

export const topicProgressSchema = z.object({
  slug: z.string(),
  title: z.string(),
  total: z.number(),
  solved: z.number(),
})

export const trackProgressSchema = z.object({
  slug: z.string(),
  title: z.string(),
  total: z.number(),
  solved: z.number(),
  next_task_slug: z.string().nullable().default(null),
})

export const difficultyProgressSchema = z.object({ total: z.number(), solved: z.number() })

export const solvedTaskSchema = z.object({
  slug: z.string(),
  title: z.string(),
  solved_at: z.string().nullable(),
})

export const reviewDebtSchema = z.object({
  slug: z.string(),
  title: z.string(),
  due_at: z.string(),
})

export const progressSchema = z.object({
  totals: progressTotalsSchema,
  by_topic: z.array(topicProgressSchema),
  by_track: z.array(trackProgressSchema),
  by_difficulty: z.record(z.string(), difficultyProgressSchema),
  recent_solved: z.array(solvedTaskSchema),
  review_debt: z.array(reviewDebtSchema),
})

export type Progress = z.infer<typeof progressSchema>
export type TopicProgress = z.infer<typeof topicProgressSchema>
export type TrackProgress = z.infer<typeof trackProgressSchema>
export type DifficultyProgress = z.infer<typeof difficultyProgressSchema>
export type SolvedTask = z.infer<typeof solvedTaskSchema>
export type ReviewDebt = z.infer<typeof reviewDebtSchema>

export const progressKeys = {
  me: (viewer: string | null) => ['progress', 'me', viewer] as const,
}

export async function fetchProgress(): Promise<Progress> {
  return progressSchema.parse(await authFetch<unknown>('/me/progress'))
}

export function useProgress() {
  const viewer = useViewerId()
  return useQuery({
    queryKey: progressKeys.me(viewer),
    queryFn: fetchProgress,
    enabled: viewer !== null,
  })
}
